// App state machine + derived guidance. The phases mirror the driver's flow:
//   idle (browse/pick a field) → field (set width, generate) → course (review, GO) → driving.
import { create } from 'zustand';
import {
  abFromField,
  bestDirection,
  computeGuidance,
  contourCourse,
  courseStats,
  coverage,
  fieldKey,
  generateSwaths,
  longestEdgeBearing,
  subtractCircles,
  type AvoidCircle,
  type Course,
  type CourseStats,
  type GnssFix,
  type Guidance,
  type Lane,
  type LonLat,
} from '@fla/core';
import type { MultiPolygon, Polygon } from 'geojson';
import {
  deleteCourse,
  deleteImplement,
  fetchConfig,
  finishSession,
  getActiveSession,
  getSettings,
  listCourses,
  listImplements,
  saveCourse,
  saveImplement,
  saveSession,
  saveSettings,
  type AppConfig,
  type FieldCollection,
  type FieldFeature,
  type Implement,
  type SavedCourse,
  type WorkSession,
  type WorkSessionInput,
} from './api';
import { startGeolocationProvider, type ProviderError, type ProviderHandle } from './provider';
import { initAuth, logout as authLogout } from './auth';
import { mapRef } from './map/mapRef';
import { clientSim } from './sim/clientSim';
import { simDrive, simStop } from './sim/simControl';
import { lineString, point, nearestPointOnLine } from '@turf/turf';

/** Beta-test fallback location (near Würzburg) — also the map's pre-fix center (see MapView). */
const DEFAULT_CENTER: LonLat = [9.97967, 49.92631];

/** Persisted across reloads so the testing simulator stays on until explicitly turned off. */
const SIM_ENABLED_KEY = 'fla.simEnabled';
function readSimEnabled(): boolean {
  try {
    return globalThis.localStorage?.getItem(SIM_ENABLED_KEY) === '1';
  } catch {
    return false;
  }
}

export type Phase = 'idle' | 'field' | 'course' | 'driving' | 'paused';
export type GuidanceUi = 'arrow' | 'lightbar';
export type EditMode = 'none' | 'drawField' | 'editField' | 'addZone';
/** Course shape: straight parallel swaths (AB line) or boundary-parallel concentric rings. */
export type CourseMode = 'parallel' | 'contour';

/** Recompute coverage every Nth fix while driving — turf buffer over the whole trail is heavy. */
const COVERAGE_EVERY = 8;

const DEFAULT_ZONE_RADIUS_M = 10;

/** Debounce window for autosaving the driving session — coalesces the 5 Hz fix stream into
 *  ~1 write/1.5 s instead of hammering the server with a growing trail. */
const AUTOSAVE_MS = 1500;
let autosaveTimer: ReturnType<typeof setTimeout> | null = null;
function cancelAutosave(): void {
  if (autosaveTimer) {
    clearTimeout(autosaveTimer);
    autosaveTimer = null;
  }
}

/** Handle for the active browser-geolocation provider (this device feeding GPS). Not store state —
 *  it isn't serializable; the store only tracks whether it's active. */
let providerHandle: ProviderHandle | null = null;

/** Client-generated session id so every autosave is an idempotent upsert (no duplicate sessions
 *  from the create/first-fix race). randomUUID needs a secure context; fall back when insecure. */
function genId(): string {
  const c = globalThis.crypto as Crypto | undefined;
  if (c && typeof c.randomUUID === 'function') {
    try {
      return c.randomUUID();
    } catch {
      /* insecure context (http LAN) — fall through */
    }
  }
  return `sess-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/** Snapshot the current driving state into a session upsert payload (null if no live session). */
function buildSessionInput(s: State, status: WorkSession['status']): WorkSessionInput | null {
  if (!s.selectedField || !s.currentSessionId) return null;
  return {
    id: s.currentSessionId,
    fieldKey: fieldKey(fieldRing(s.selectedField)),
    field: s.selectedField,
    activeCourseId: null,
    widthM: s.widthM,
    headingDeg: s.headingDeg ?? 0,
    headlandRounds: s.headlandRounds,
    courseMode: s.courseMode,
    trail: s.trail,
    lastActiveLaneIndex: s.lastActiveLaneIndex,
    status,
  };
}

/** Save the session — debounced during driving, immediate on pause/stop. The client-generated id
 *  makes every save an idempotent upsert, so there's no create/first-fix race. */
function persistSession(input: WorkSessionInput | null, debounce: boolean): void {
  if (!input) return;
  cancelAutosave();
  if (debounce) {
    autosaveTimer = setTimeout(() => {
      autosaveTimer = null;
      void saveSession(input);
    }, AUTOSAVE_MS);
  } else {
    void saveSession(input);
  }
}

function syntheticField(ring: LonLat[], name: string): FieldFeature {
  const closed = ring.slice();
  const first = closed[0];
  const last = closed[closed.length - 1];
  if (first && last && (first[0] !== last[0] || first[1] !== last[1])) closed.push(first);
  return {
    type: 'Feature',
    properties: { name },
    geometry: { type: 'Polygon', coordinates: [closed as number[][]] },
  };
}

function fieldRing(f: FieldFeature): LonLat[] {
  return (f.geometry.coordinates[0] ?? []) as LonLat[];
}

function nearestLane(course: Course, fix: GnssFix): Lane | null {
  const g = computeGuidance(course, { lat: fix.lat, lon: fix.lon });
  if (!g) return null;
  return course.lanes.find((l) => l.index === g.activeLaneIndex) ?? course.lanes[0] ?? null;
}

interface State {
  phase: Phase;
  connected: boolean;
  fix: GnssFix | null;
  fields: FieldCollection | null;
  fieldsSource: FieldCollection['source'] | null;
  selectedField: FieldFeature | null;
  widthM: number;
  headingDeg: number | undefined;
  headlandRounds: number;
  courseMode: CourseMode;
  course: Course | null;
  /** Distance/turn/time stats for the current course preview. */
  stats: CourseStats | null;
  guidance: Guidance | null;
  guidanceUi: GuidanceUi;
  savedCourses: SavedCourse[];
  /** Saved implements (tools) for the current user. */
  implements: Implement[];
  /** Server-advertised runtime config (auth, gnss source, ingest), fetched on boot. */
  serverConfig: AppConfig | null;
  /** Boot probe finished (config fetched + auth init). Until then, show a splash. */
  bootDone: boolean;
  /** Whether this device is currently providing GPS (browser geolocation → server). */
  providerActive: boolean;
  providerError: ProviderError | null;
  /** Live horizontal accuracy of this device's GPS while providing, meters. */
  providerAccuracyM: number | null;
  /** Whether the driving chase-camera is actively following the vehicle (false while the user pans). */
  cameraFollowing: boolean;
  setCameraFollowing: (following: boolean) => void;
  /** Show the simulator nudge controls (dev-testing only; only meaningful when the sim is active). */
  showSimControls: boolean;
  setShowSimControls: (show: boolean) => void;
  /** Browser-side test simulator on (works on cloud/phone deployments that run no server sim). */
  simEnabled: boolean;
  setSimEnabled: (on: boolean) => void;
  /** (Re)start the browser sim at the current location. Called on map-ready when simEnabled persisted. */
  startClientSim: () => void;
  /** Breadcrumb of actually-driven positions [lon,lat], recorded while driving. */
  trail: LonLat[];
  /** Worked-area coverage while driving. */
  coveragePct: number;
  coverageM2: number;
  coverageGeo: Polygon | MultiPolygon | null;
  /** Id of the work-session being driven/paused (null when not in a session). */
  currentSessionId: string | null;
  /** Last lane index followed — persisted with the session, cosmetic on resume. */
  lastActiveLaneIndex: number | null;
  /** Unfinished session offered on the selected field (Resume / Start fresh), or null. */
  pendingResume: WorkSession | null;
  editMode: EditMode;
  /** Bumped when the edited ring's vertex COUNT changes (insert/remove) so the map recreates drag
   *  handles — without recreating them on every drag (which moves a vertex but keeps the count). */
  editRev: number;
  /** Vertices being tapped out while drawing a field. */
  draftRing: LonLat[];
  /** Avoidance (no-drive) zones cut out of the field. */
  zones: AvoidCircle[];
  /** The zone the user tapped (for resize/move/delete), or null. */
  selectedZoneIndex: number | null;

  setConnected: (up: boolean) => void;
  setFix: (fix: GnssFix) => void;
  setFields: (fc: FieldCollection) => void;
  selectField: (f: FieldFeature) => void;
  setWidth: (m: number) => void;
  setHeading: (deg: number | undefined) => void;
  /** Set heading from a dragged arrow bearing (snaps to field edges) + live course preview. */
  setHeadingFromDrag: (rawBearing: number) => void;
  setHeadlandRounds: (n: number) => void;
  /** Switch parallel ↔ contour course shape (+ live re-preview). */
  setCourseMode: (m: CourseMode) => void;
  /** Auto-pick the AB direction that minimises turns/length (fewest, longest passes). */
  optimize: () => void;
  /** Rebuild the course preview from the current config, without changing phase. */
  regen: () => void;
  generateCourse: () => void;
  startDriving: () => void;
  /** Pause the session: freeze trail + coverage accumulation, keep the HUD live. */
  pauseDriving: () => void;
  /** Resume a paused session: continue driving + accumulating. */
  resumeDriving: () => void;
  stopDriving: () => void;
  /** Restore the offered `pendingResume` session (trail + coverage) and continue driving. */
  acceptResume: () => void;
  /** Dismiss the resume offer (start fresh keeps it dismissed). */
  discardResume: () => void;
  setGuidanceUi: (ui: GuidanceUi) => void;
  backToBrowse: () => void;
  /** Course → back to field configuration (keeps the field + preview). */
  backToField: () => void;

  loadSavedCourses: () => Promise<void>;
  saveCurrentCourse: (name: string) => Promise<void>;
  loadCourse: (saved: SavedCourse) => void;
  deleteSavedCourse: (id: string) => Promise<void>;

  /** Hydrate per-user preferences (guidance UI) from the server on boot. */
  loadSettings: () => Promise<void>;
  loadImplements: () => Promise<void>;
  /** Fetch server config (auth/gnss/ingest) on boot. */
  loadServerConfig: () => Promise<void>;
  /** Boot: fetch config; in cloud mode init Keycloak (redirects to the hosted login if needed),
   *  then load data. In standalone mode just load data. */
  bootstrap: () => Promise<void>;
  /** Cloud logout → redirect to Keycloak logout. */
  logout: () => void;
  /** Start/stop providing this device's GPS to the server. */
  startProvider: () => void;
  stopProvider: () => void;
  /** Apply a saved implement's working width to the current field config. */
  applyImplement: (id: string) => void;
  /** Save the current working width as a named implement. */
  saveCurrentImplement: (name: string) => Promise<void>;
  deleteSavedImplement: (id: string) => Promise<void>;

  // Editor
  startDrawField: () => void;
  startEditField: () => void;
  startAddZone: () => void;
  cancelEdit: () => void;
  handleMapClick: (p: LonLat) => void;
  undoDraft: () => void;
  finishDrawField: () => void;
  updateFieldVertex: (index: number, p: LonLat) => void;
  /** Remove a vertex (by handle index) from the edited outline; keeps a triangle minimum. */
  removeFieldVertex: (index: number) => void;
  finishEdit: () => void;
  /** Select a zone for editing (resize/move/delete). null clears the selection. */
  selectZone: (index: number | null) => void;
  /** Set a specific zone's radius (meters). */
  setZoneRadius: (index: number, r: number) => void;
  /** Move a specific zone's center. */
  updateZoneCenter: (index: number, p: LonLat) => void;
  /** Delete a single zone. */
  removeZone: (index: number) => void;
  clearZones: () => void;
}

export const useStore = create<State>()((set, get) => ({
  phase: 'idle',
  connected: false,
  fix: null,
  fields: null,
  fieldsSource: null,
  selectedField: null,
  widthM: 6,
  headingDeg: undefined,
  headlandRounds: 0,
  courseMode: 'parallel',
  course: null,
  stats: null,
  guidance: null,
  guidanceUi: 'arrow',
  savedCourses: [],
  implements: [],
  serverConfig: null,
  bootDone: false,
  providerActive: false,
  providerError: null,
  providerAccuracyM: null,
  cameraFollowing: true,
  showSimControls: true,
  simEnabled: readSimEnabled(),
  trail: [],
  coveragePct: 0,
  coverageM2: 0,
  coverageGeo: null,
  currentSessionId: null,
  lastActiveLaneIndex: null,
  pendingResume: null,
  editMode: 'none',
  editRev: 0,
  draftRing: [],
  zones: [],
  selectedZoneIndex: null,

  setConnected: (up) => set({ connected: up }),

  setFix: (fix) => {
    const { course, phase, trail, selectedField } = get();
    // Widen the "on line" band to the fix's own accuracy so a metre-level phone source doesn't
    // flip steer left/right forever; cm-accurate RTK/sim fixes (no accuracyM) keep the 5 cm default.
    const guidance = course
      ? computeGuidance(
          course,
          fix,
          fix.accuracyM != null ? { onThresholdM: Math.max(0.1, fix.accuracyM / 2) } : {},
        )
      : null;
    if (phase !== 'driving') {
      set({ fix, guidance });
      return;
    }
    const nextTrail: LonLat[] = [...trail, [fix.lon, fix.lat] as LonLat].slice(-4000);
    set({
      fix,
      guidance,
      trail: nextTrail,
      lastActiveLaneIndex: guidance?.activeLaneIndex ?? get().lastActiveLaneIndex,
    });
    // Worked-area coverage: throttled — turf buffer over the whole trail is heavy.
    if (course && selectedField && nextTrail.length % COVERAGE_EVERY === 0) {
      const cov = coverage(nextTrail, course.widthM, fieldRing(selectedField));
      set({ coveragePct: cov.pct, coverageM2: cov.workedM2, coverageGeo: cov.geometry });
    }
    // Autosave the resumable state (debounced — coalesces the fix stream).
    persistSession(buildSessionInput(get(), 'active'), true);
  },

  setFields: (fc) => set({ fields: fc, fieldsSource: fc.source }),

  selectField: (f) => {
    set({
      selectedField: f,
      phase: 'field',
      course: null,
      guidance: null,
      zones: [],
      selectedZoneIndex: null,
      editMode: 'none',
      draftRing: [],
      headingDeg: longestEdgeBearing(fieldRing(f)),
      pendingResume: null,
    });
    get().regen(); // show a preview course immediately
    // Smoothly frame the chosen field (fully in view + margin) from wherever the camera was.
    mapRef.current?.fitTo({ type: 'FeatureCollection', features: [f], source: 'sample' });
    // Offer to resume unfinished work on this field (don't auto-restore).
    const key = fieldKey(fieldRing(f));
    void getActiveSession(key).then((s) => {
      const cur = get().selectedField;
      if (s && cur && fieldKey(fieldRing(cur)) === key && get().phase === 'field') {
        set({ pendingResume: s });
      }
    });
  },

  setWidth: (m) => {
    set({ widthM: Math.max(0.5, Math.round(m * 2) / 2) });
    get().regen();
  },
  setHeading: (deg) => {
    set({ headingDeg: deg });
    get().regen();
  },

  // Heading already snapped by the on-map arrow; just apply + re-preview.
  setHeadingFromDrag: (bearing) => {
    set({ headingDeg: bearing });
    get().regen();
  },

  setHeadlandRounds: (n) => {
    set({ headlandRounds: Math.max(0, Math.min(8, Math.round(n))) });
    get().regen();
  },

  setCourseMode: (m) => {
    set({ courseMode: m });
    get().regen();
  },

  // Auto-pick the AB direction with the fewest turns (then shortest) → most driving on straight
  // lines. Switches to parallel mode (the optimizer only reasons about straight swaths).
  optimize: () => {
    const { selectedField, widthM, headlandRounds } = get();
    if (!selectedField) return;
    const best = bestDirection(fieldRing(selectedField), widthM, headlandRounds * widthM);
    set({ headingDeg: best.headingDeg, courseMode: 'parallel' });
    get().regen();
  },

  /** Rebuild the course preview from the current config; keeps the current phase. */
  regen: () => {
    const { selectedField, widthM, headingDeg, headlandRounds, zones, courseMode } = get();
    if (!selectedField) {
      set({ course: null, stats: null });
      return;
    }
    const ring = fieldRing(selectedField);
    const fieldPoly = zones.length > 0 ? subtractCircles(ring, zones) : ring;
    const headlandM = headlandRounds * widthM;
    const course =
      courseMode === 'contour'
        ? contourCourse(fieldPoly, widthM)
        : ((): Course => {
            const ab = abFromField(ring, headingDeg);
            return generateSwaths(
              fieldPoly,
              ab.a,
              ab.b,
              widthM,
              headlandM > 0 ? { headlandM } : {},
            );
          })();
    set({ course, stats: courseStats(course) });
  },

  generateCourse: () => {
    get().regen();
    if (get().course) set({ phase: 'course', editMode: 'none', draftRing: [] });
  },

  startDriving: () => {
    const { course, fix, selectedField, pendingResume } = get();
    if (!course || course.lanes.length === 0 || !selectedField) return;
    // Starting fresh on a field that had unfinished work finalizes the old session first
    // (keeps the one-open-session-per-field invariant).
    if (pendingResume) void finishSession(pendingResume.id);
    const lane = fix ? nearestLane(course, fix) : course.lanes[0];
    if (lane) void simDrive(lane.coordinates, { speedMps: 2.5, offsetM: 0 });
    set({
      phase: 'driving',
      trail: [],
      coveragePct: 0,
      coverageM2: 0,
      coverageGeo: null,
      currentSessionId: genId(),
      lastActiveLaneIndex: lane?.index ?? null,
      pendingResume: null,
    });
    persistSession(buildSessionInput(get(), 'active'), false); // create immediately
  },

  pauseDriving: () => {
    void simStop();
    set({ phase: 'paused' });
    persistSession(buildSessionInput(get(), 'paused'), false);
  },

  resumeDriving: () => {
    const { course, fix } = get();
    if (course && course.lanes.length > 0) {
      const lane = fix ? nearestLane(course, fix) : course.lanes[0];
      if (lane) void simDrive(lane.coordinates, { speedMps: 2.5, offsetM: 0 });
    }
    set({ phase: 'driving' });
    persistSession(buildSessionInput(get(), 'active'), false);
  },

  stopDriving: () => {
    void simStop();
    cancelAutosave(); // don't let a queued autosave re-open the finished session
    const id = get().currentSessionId;
    if (id) void finishSession(id);
    set({ phase: 'course', currentSessionId: null, pendingResume: null });
  },

  acceptResume: () => {
    const { pendingResume, selectedField } = get();
    if (!pendingResume || !selectedField) return;
    const trail = pendingResume.trail ?? [];
    const widthM = pendingResume.widthM > 0 ? pendingResume.widthM : get().widthM;
    // Restore the full course config so resume rebuilds the SAME lanes, not a default course.
    set({
      widthM,
      headingDeg: pendingResume.headingDeg,
      headlandRounds: pendingResume.headlandRounds,
      courseMode: pendingResume.courseMode,
    });
    get().regen(); // rebuild the original course at the restored config
    const course = get().course;
    const cov =
      course && trail.length >= 2 ? coverage(trail, course.widthM, fieldRing(selectedField)) : null;
    const fix = get().fix;
    if (course && course.lanes.length > 0) {
      const lane = fix ? nearestLane(course, fix) : course.lanes[0];
      if (lane) void simDrive(lane.coordinates, { speedMps: 2.5, offsetM: 0 });
    }
    set({
      phase: 'driving',
      currentSessionId: pendingResume.id,
      trail,
      coveragePct: cov?.pct ?? 0,
      coverageM2: cov?.workedM2 ?? 0,
      coverageGeo: cov?.geometry ?? null,
      lastActiveLaneIndex: pendingResume.lastActiveLaneIndex,
      pendingResume: null,
    });
    persistSession(buildSessionInput(get(), 'active'), false);
  },

  discardResume: () => {
    // "Start fresh" finalizes the old session so it can't linger as a second open session.
    const { pendingResume } = get();
    if (pendingResume) void finishSession(pendingResume.id);
    set({ pendingResume: null });
  },

  setGuidanceUi: (ui) => {
    set({ guidanceUi: ui });
    void saveSettings({ guidanceUi: ui }); // persist preference (no-op visually if it fails)
  },

  backToBrowse: () =>
    set({
      phase: 'idle',
      selectedField: null,
      course: null,
      guidance: null,
      trail: [],
      zones: [],
      selectedZoneIndex: null,
      editMode: 'none',
      draftRing: [],
      currentSessionId: null,
      pendingResume: null,
    }),

  backToField: () => set({ phase: 'field', editMode: 'none' }),

  loadSavedCourses: async () => set({ savedCourses: await listCourses() }),

  saveCurrentCourse: async (name) => {
    const { selectedField, widthM, headingDeg, headlandRounds } = get();
    if (!selectedField) return;
    await saveCourse({
      name,
      field: selectedField,
      widthM,
      headingDeg: headingDeg ?? 0,
      headlandM: headlandRounds * widthM,
    });
    await get().loadSavedCourses();
  },

  loadCourse: (saved) => {
    const rounds = saved.widthM > 0 ? Math.round(saved.headlandM / saved.widthM) : 0;
    set({
      selectedField: saved.field,
      widthM: saved.widthM,
      headingDeg: saved.headingDeg,
      headlandRounds: rounds,
      courseMode: 'parallel',
      phase: 'field',
    });
    get().generateCourse();
    mapRef.current?.fitTo({ type: 'FeatureCollection', features: [saved.field], source: 'cache' });
  },

  deleteSavedCourse: async (id) => {
    await deleteCourse(id);
    await get().loadSavedCourses();
  },

  loadSettings: async () => {
    const s = await getSettings();
    if (s) set({ guidanceUi: s.guidanceUi });
  },

  loadImplements: async () => set({ implements: await listImplements() }),

  loadServerConfig: async () => set({ serverConfig: await fetchConfig() }),

  bootstrap: async () => {
    const cfg = await fetchConfig();
    set({ serverConfig: cfg });
    if (cfg?.authRequired && cfg.oidc) {
      // Redirects to the hosted login if not signed in; resolves authenticated on return.
      await initAuth(cfg.oidc);
    }
    set({ bootDone: true });
    await Promise.all([get().loadSavedCourses(), get().loadSettings(), get().loadImplements()]);
  },

  logout: () => {
    get().stopProvider();
    authLogout(); // redirects to Keycloak logout, then back to the app (→ login)
  },

  startProvider: () => {
    if (providerHandle) return;
    set({ providerError: null });
    providerHandle = startGeolocationProvider(
      (e) => {
        providerHandle = null;
        set({ providerError: e, providerActive: false });
      },
      (m) => set({ providerAccuracyM: m }),
    );
    set({ providerActive: providerHandle !== null });
  },

  stopProvider: () => {
    providerHandle?.stop();
    providerHandle = null;
    set({ providerActive: false, providerAccuracyM: null });
  },

  setCameraFollowing: (following) => set({ cameraFollowing: following }),

  setShowSimControls: (show) => set({ showSimControls: show }),

  setSimEnabled: (on) => {
    try {
      globalThis.localStorage?.setItem(SIM_ENABLED_KEY, on ? '1' : '0');
    } catch {
      /* private mode / no storage — fine, just won't persist */
    }
    set({ simEnabled: on });
    if (on) get().startClientSim();
    else clientSim.stop(); // leave `connected` to the live WS status (onStatus tracks the real socket)
  },

  startClientSim: () => {
    const fix = get().fix;
    const c = mapRef.current?.map.getCenter();
    const center: LonLat = fix ? [fix.lon, fix.lat] : c ? [c.lng, c.lat] : DEFAULT_CENTER;
    clientSim.start(center, (f) => get().setFix(f));
    set({ connected: true });
  },

  applyImplement: (id) => {
    const impl = get().implements.find((i) => i.id === id);
    if (!impl) return;
    get().setWidth(impl.widthM); // setWidth clamps + re-previews the course
  },

  saveCurrentImplement: async (name) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    await saveImplement({ name: trimmed, widthM: get().widthM, offsetM: 0 });
    await get().loadImplements();
  },

  deleteSavedImplement: async (id) => {
    await deleteImplement(id);
    await get().loadImplements();
  },

  // --- Editor ---
  startDrawField: () =>
    set({ editMode: 'drawField', draftRing: [], selectedField: null, course: null, phase: 'idle' }),

  startEditField: () => {
    if (get().selectedField) set({ editMode: 'editField', course: null, phase: 'field' });
  },

  startAddZone: () => {
    if (get().selectedField) set({ editMode: 'addZone', selectedZoneIndex: null });
  },

  cancelEdit: () => set({ editMode: 'none', draftRing: [], selectedZoneIndex: null }),

  handleMapClick: (p) => {
    const { editMode } = get();
    if (editMode === 'drawField') {
      set({ draftRing: [...get().draftRing, p] });
    } else if (editMode === 'addZone') {
      const zones = [...get().zones, { center: p, radiusM: DEFAULT_ZONE_RADIUS_M }];
      set({ zones, selectedZoneIndex: zones.length - 1 }); // select the new one for resize/move/delete
      get().regen();
    } else if (editMode === 'editField') {
      // Tap on (near) an edge → insert a new vertex there, so a fresh drag handle appears.
      const f = get().selectedField;
      if (!f) return;
      const ring = (f.geometry.coordinates[0] ?? []) as LonLat[];
      if (ring.length < 2) return;
      const snapped = nearestPointOnLine(lineString(ring), point(p));
      const segIndex = snapped.properties.index ?? -1; // start vertex of the closest segment
      const distM = (snapped.properties.dist ?? Infinity) * 1000; // turf dist is km
      // Only insert when the tap actually lands on the boundary (≈14 px tolerance at this zoom).
      const tolM = (mapRef.current?.metersPerPixel(p[1]) ?? 1) * 14;
      if (segIndex < 0 || distM > tolM) return;
      const newPt = snapped.geometry.coordinates as LonLat;
      const nextRing = [...ring.slice(0, segIndex + 1), newPt, ...ring.slice(segIndex + 1)];
      set({
        selectedField: {
          ...f,
          geometry: { type: 'Polygon', coordinates: [nextRing as number[][]] },
        },
        editRev: get().editRev + 1,
      });
      get().regen();
    }
  },

  undoDraft: () => set({ draftRing: get().draftRing.slice(0, -1) }),

  finishDrawField: () => {
    const { draftRing } = get();
    if (draftRing.length < 3) return;
    get().selectField(syntheticField(draftRing, 'Eigenes Feld'));
  },

  updateFieldVertex: (index, p) => {
    const { selectedField } = get();
    if (!selectedField) return;
    const ring = (selectedField.geometry.coordinates[0] ?? []).slice() as LonLat[];
    if (index < 0 || index >= ring.length) return;
    ring[index] = p;
    // keep the ring closed (first === last)
    if (index === 0 && ring.length > 1) ring[ring.length - 1] = p;
    set({
      selectedField: {
        ...selectedField,
        geometry: { type: 'Polygon', coordinates: [ring as number[][]] },
      },
    });
    get().regen();
  },

  removeFieldVertex: (index) => {
    const { selectedField } = get();
    if (!selectedField) return;
    // The ring is closed (first === last); edit handles are the distinct vertices (closing dropped).
    const distinct = ((selectedField.geometry.coordinates[0] ?? []) as LonLat[]).slice(0, -1);
    if (distinct.length <= 3) return; // keep at least a triangle
    if (index < 0 || index >= distinct.length) return;
    distinct.splice(index, 1);
    const nextRing = [...distinct, distinct[0]!]; // re-close
    set({
      selectedField: {
        ...selectedField,
        geometry: { type: 'Polygon', coordinates: [nextRing as number[][]] },
      },
      editRev: get().editRev + 1,
    });
    get().regen();
  },

  finishEdit: () => set({ editMode: 'none', selectedZoneIndex: null }),

  selectZone: (index) => set({ selectedZoneIndex: index }),

  setZoneRadius: (index, r) => {
    const { zones } = get();
    if (index < 0 || index >= zones.length) return;
    const next = zones.slice();
    next[index] = { ...next[index]!, radiusM: Math.max(1, Math.round(r)) };
    set({ zones: next });
    get().regen();
  },

  updateZoneCenter: (index, p) => {
    const { zones } = get();
    if (index < 0 || index >= zones.length) return;
    const next = zones.slice();
    next[index] = { ...next[index]!, center: p };
    set({ zones: next });
    get().regen();
  },

  removeZone: (index) => {
    const { zones } = get();
    if (index < 0 || index >= zones.length) return;
    set({ zones: zones.filter((_, i) => i !== index), selectedZoneIndex: null });
    get().regen();
  },

  clearZones: () => {
    set({ zones: [], selectedZoneIndex: null });
    get().regen();
  },
}));

// Test/debug hook: lets UI-verify (Playwright) and the preview drive the flow deterministically
// over the map canvas. Harmless in production.
if (typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).__flaStore = useStore;
}
